/**
=========================================================
* Structured data (JSON-LD) builders
=========================================================
* Produces schema.org objects for rich results. Data is sourced from
* propertyData so the markup stays in sync with the visible content.
*/

import siteConfig from "config/site";
import propertyData from "pages/Home/sections/data/propertyData";

const { siteUrl, siteName, socialImage, listings, contactEmail } = siteConfig;

const lodgingId = `${siteUrl}/#lodging`;
const organizationId = `${siteUrl}/#organization`;
const websiteId = `${siteUrl}/#website`;

// Flatten categorised amenities into schema.org LocationFeatureSpecification.
const amenityFeatures = propertyData.amenities
  .flatMap((group) => group.items)
  .map((name) => ({
    "@type": "LocationFeatureSpecification",
    name,
    value: true,
  }));

export const organizationSchema = {
  "@type": "Organization",
  "@id": organizationId,
  name: siteName,
  url: siteUrl,
  logo: `${siteUrl}/toucan-favicon.png`,
  image: socialImage,
  email: contactEmail,
  sameAs: [listings.airbnb, listings.vrbo],
};

export const websiteSchema = {
  "@type": "WebSite",
  "@id": websiteId,
  url: siteUrl,
  name: siteName,
  description: siteConfig.defaultDescription,
  publisher: { "@id": organizationId },
  inLanguage: "en",
};

// LodgingBusiness carries aggregateRating, address, geo, amenities, etc.
export const lodgingSchema = {
  "@type": ["LodgingBusiness", "VacationRental"],
  "@id": lodgingId,
  name: propertyData.name,
  description: propertyData.tagline,
  url: siteUrl,
  image: [socialImage],
  email: contactEmail,
  priceRange: "$$$",
  currenciesAccepted: "USD",
  numberOfRooms: propertyData.highlights.bedrooms,
  petsAllowed: true,
  smokingAllowed: false,
  sameAs: [listings.airbnb, listings.vrbo],
  address: {
    "@type": "PostalAddress",
    streetAddress: propertyData.location.address,
    addressLocality: propertyData.location.city,
    addressRegion: propertyData.location.state,
    addressCountry: "CR",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: propertyData.location.coordinates.lat,
    longitude: propertyData.location.coordinates.lng,
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: propertyData.highlights.rating,
    reviewCount: propertyData.highlights.reviews,
    bestRating: 5,
    worstRating: 1,
  },
  amenityFeature: amenityFeatures,
  containsPlace: {
    "@type": "Accommodation",
    additionalType: "EntirePlace",
    numberOfBedrooms: propertyData.highlights.bedrooms,
    numberOfBathroomsTotal: propertyData.highlights.bathrooms,
    occupancy: {
      "@type": "QuantitativeValue",
      maxValue: propertyData.highlights.guests,
      unitText: "guests",
    },
  },
  publisher: { "@id": organizationId },
};

/**
 * Build a BreadcrumbList for a page.
 * @param {Array<{name: string, path: string}>} items ordered crumbs.
 */
export function buildBreadcrumbSchema(items) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.path}`,
    })),
  };
}

/**
 * Wrap one or more schema nodes in a single @graph document.
 */
export function graph(...nodes) {
  return {
    "@context": "https://schema.org",
    "@graph": nodes.flat().filter(Boolean),
  };
}

export default graph;
